import { useMemo, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { askCompetitiveExamAi, type ResponseLanguage } from "./ai-service";

/** Cute emoji as Unicode escapes (reliable on Windows; shows colorful emoji in modern browsers) */
const HI_BUBBLE = "\u{1F43B}"; // teddy bear
const SMILEY = "\u{1F60A}"; // smiling face with smiling eyes
const BLUSH_STARS = "\u2B50"; // smiling face with star
const TEDDY_BOOK = "\u{1F43B}\u{1F4D6}"; // teddy + book
const SMART = "\u{1F9E0}\u{1F4A1}"; // brain + light bulb

const TOPIC_OPTIONS = [
  "Indian History",
  "Indian State History",
  "Indian Constitution",
  "Gujarati Grammar",
  "English Grammar",
  "Current Affairs",
  "Geography",
  "Polity",
];

function FormattedResponse({ content }: { content: string }) {
  if (!content.trim()) {
    return (
      <p className="text-sm text-muted-foreground">
        Your answer from Sanmaya will show here after you ask a question.
      </p>
    );
  }

  const lines = content.split("\n");

  return (
    <div className="space-y-2 text-sm leading-6">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-2" />;
        if (trimmed.startsWith("### ")) return <h3 key={index} className="text-base font-semibold">{trimmed.slice(4)}</h3>;
        if (trimmed.startsWith("## ")) return <h2 key={index} className="text-lg font-semibold">{trimmed.slice(3)}</h2>;
        if (trimmed.startsWith("# ")) return <h1 key={index} className="text-xl font-semibold">{trimmed.slice(2)}</h1>;
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) return <li key={index} className="ml-5 list-disc">{trimmed.slice(2)}</li>;
        if (/^\d+\.\s/.test(trimmed)) return <li key={index} className="ml-5 list-decimal">{trimmed.replace(/^\d+\.\s/, "")}</li>;
        return <p key={index} className="whitespace-pre-wrap">{line}</p>;
      })}
    </div>
  );
}

function ResponseLoader() {
  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 p-4">
      <div className="mb-3 flex items-center gap-2 text-primary">
        <Loader2 className="h-4 w-4 animate-spin" />
        <Sparkles className="h-4 w-4" />
        <p className="text-sm font-medium">
          <span aria-hidden className="mr-1">
            {"\u{1F4AB}\u{1F43B}"}
          </span>
          Sanmaya is thinking…
        </p>
      </div>

      <div className="space-y-2">
        <div className="h-3 w-[92%] animate-pulse rounded bg-muted" />
        <div className="h-3 w-[84%] animate-pulse rounded bg-muted" />
        <div className="h-3 w-[76%] animate-pulse rounded bg-muted" />
        <div className="h-3 w-[88%] animate-pulse rounded bg-muted" />
        <div className="h-3 w-[62%] animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

export function AIAssistantGrid() {
  const [message, setMessage] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [responseLanguage, setResponseLanguage] = useState<ResponseLanguage>("auto");
  const [selectedTopics, setSelectedTopics] = useState<string[]>([
    "Indian History",
    "Indian Constitution",
    "Gujarati Grammar",
    "English Grammar",
  ]);

  const canAsk = useMemo(() => message.trim().length > 0 && !loading, [message, loading]);

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((item) => item !== topic) : [...prev, topic]
    );
  };

  const askAi = async () => {
    if (!canAsk) return;
    setLoading(true);
    setResponse("");

    try {
      const answer = await askCompetitiveExamAi({
        message: message.trim(),
        responseLanguage,
        topics: selectedTopics,
      });
      setResponse(answer || "No response generated.");
    } catch (error) {
      setResponse(error instanceof Error ? error.message : "Sanmaya AI request failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="p-4 sm:p-6 lg:p-8">
      <div className="mb-4 space-y-4">
        <div>
          <h1 className="text-2xl font-bold">
            Sanmaya AI{" "}
            <span className="text-2xl font-normal" aria-hidden>
              {"\u2728"}
            </span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your soft &amp; silly exam buddy — less panic, more &ldquo;yay, I get it!&rdquo;{" "}
            <span className="inline-block align-middle text-lg leading-none" aria-hidden>
              {SMILEY}
            </span>
          </p>
        </div>

        <div className="rounded-xl border border-primary/25 bg-gradient-to-br from-primary/8 via-background to-background px-4 py-5 shadow-sm sm:px-5">
          <div className="flex gap-3">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/15 text-3xl leading-none shadow-inner ring-2 ring-primary/10"
              aria-hidden
            >
              {HI_BUBBLE}
            </div>
            <div className="min-w-0 space-y-2">
              <p className="text-base font-medium leading-snug text-foreground">
                Hiii! I&apos;m{" "}
                <span className="inline-block rounded-md bg-primary px-2 py-0.5 text-base font-semibold text-primary-foreground shadow-sm ring-1 ring-black/10 dark:ring-white/15">
                  Sanmaya
                </span>{" "}
                — tiny brain, big cheerleader energy for your
                syllabus, with a little extra smart backup{" "}
                <span className="inline-block align-middle text-xl leading-none" aria-hidden>
                  {BLUSH_STARS}
                </span>
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                So happy you&apos;re here{" "}
                <span className="inline-block align-middle text-lg leading-none" aria-hidden>
                  {TEDDY_BOOK}
                </span>{" "}
                — drop your question below (GK, constitution, grammar, anything fuzzy in your head). I&apos;ll answer
                like a friend, not a grumpy textbook{" "}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border bg-background p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
            Ask Question
            <span className="text-lg font-normal leading-none" aria-hidden>
              {SMART}
            </span>
          </h2>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your question in English or Gujarati (e.g. basic structure of the Constitution, 1857 revolt, grammar rules)..."
            rows={8}
          />

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select
              value={responseLanguage}
              onValueChange={(value) => setResponseLanguage(value as ResponseLanguage)}
            >
              <SelectTrigger className="sm:w-[240px]">
                <SelectValue placeholder="Response language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (same as question)</SelectItem>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="gujarati">Gujarati</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={askAi} disabled={!canAsk}>
              {loading ? "Thinking..." : "Ask Sanmaya AI"}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setMessage("");
                setResponse("");
              }}
            >
              Clear
            </Button>
          </div>

          <div className="mt-4">
            <p className="mb-2 text-sm font-medium">Exam Topic Scope</p>
            <div className="flex flex-wrap gap-2">
              {TOPIC_OPTIONS.map((topic) => {
                const selected = selectedTopics.includes(topic);
                return (
                  <Button
                    key={topic}
                    type="button"
                    variant={selected ? "default" : "outline"}
                    className="h-8 text-xs"
                    onClick={() => toggleTopic(topic)}
                  >
                    {topic}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-background p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
            Sanmaya AI response
            <span className="text-lg font-normal leading-none" aria-hidden title="Smart answer">
              {"\u{1F913}"}
            </span>
          </h2>
          <div className="max-h-[560px] overflow-y-auto rounded-md border p-3">
            {loading ? <ResponseLoader /> : <FormattedResponse content={response} />}
          </div>
        </div>
      </div>
    </section>
  );
}
